/**
 * @file src/_modulos/prenota/tabela/layouts/tabela/columns/components/general.tsx
 * @description Componentes de células específicas e funções gerais da tabela de pré-notas
 */

"use client";

import { AvatarComponent } from "@US";
import { Row } from "@tanstack/react-table";
import { useCatalogs } from "store";
import { useMemo } from "react";
import { fmt, HoverCard, HoverCardContent, HoverCardTrigger } from "visuais";
import {
  PrenotaRow,
  Actions,
  EditPrenotaDialog,
  AnexoPopover,
} from "@PN/tabela";
import { CELL_CLASSES } from "./utils";
import { Danfe } from "@/_modulos/prenota/tabela/danfe/Danfe";

/**
 * Metadados da filial
 */
export interface FilialMeta {
  nome: string;
  cor: string;
  avatarSrc: string;
}

/**
 * Hook para obter metadados da filial
 */
export function useFilialMeta(
  filialCodigo: string | null | undefined
): FilialMeta {
  const key = String(filialCodigo ?? "").trim();
  const { filiais } = useCatalogs();

  return useMemo(() => {
    const filial = filiais.getBy("codigo", key);
    const nome = filial?.nome?.trim() || "Desconhecida";

    return {
      nome,
      cor: getFilialColorClass(nome),
      avatarSrc: getFilialAvatarSrc(nome),
    };
  }, [key, filiais]);
}

/**
 * Obtém classe de cor da filial
 */
export function getFilialColorClass(nome: string): string {
  const nomeUpper = nome.toUpperCase();

  if (nomeUpper.includes("RODOPARANA")) {
    return "bg-sky-100 text-sky-500 dark:bg-sky-900/40 dark:text-sky-300 border-2 border-sky-600";
  }
  if (nomeUpper.includes("TIMBER")) {
    return "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/30 dark:text-yellow-300 border-2 border-yellow-600";
  }

  return "bg-muted text-muted-foreground";
}

/**
 * Obtém avatar da filial
 */
export function getFilialAvatarSrc(nome: string): string {
  const nomeUpper = nome.toUpperCase();

  if (nomeUpper.includes("RODOPARANA")) return "/rodo1.svg";
  if (nomeUpper.includes("TIMBER")) return "/timber.svg";

  return "";
}

/**
 * Célula de tipo
 */
export function TipoCell({ row }: { row: Row<PrenotaRow> }) {
  const { nome } = useFilialMeta(row.original.F1_FILIAL);

  return (
    <div className={`${CELL_CLASSES.column} justify-center`}>
      <div className={`capitalize font-medium ${CELL_CLASSES.responsiveText}`}>
        {row.original.F1_XTIPO}
      </div>
      <div className={CELL_CLASSES.responsiveText}>{nome}</div>
    </div>
  );
}

/**
 * Célula de usuário
 */
export function UserCell({ row }: { row: Row<PrenotaRow> }) {
  const { cor } = useFilialMeta(row.original.F1_FILIAL);
  const processedUsername = useMemo(
    () => row.original.F1_XUSRRA.toLowerCase().trim(),
    [row.original.F1_XUSRRA]
  );

  const getSoftColor = (color: string) => {
    if (color.includes("sky"))
      return "bg-sky-500/20 text-sky-600 dark:text-sky-400";
    if (color.includes("amber") || color.includes("yellow"))
      return "bg-amber-500/20 text-amber-600 dark:text-amber-400";
    return "bg-gray-500/20 text-gray-600 dark:text-gray-400";
  };

  const softColor = getSoftColor(cor);

  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger>
        <div className="relative group">
          <div
            className={`size-12 rounded-full flex items-center justify-center font-medium transition-all duration-300 ${softColor} group-hover:ring-2 group-hover:ring-foreground/30 ${CELL_CLASSES.responsiveText}`}
          >
            {fmt.strings.initials(row.original.F1_XUSRRA)}
          </div>

          <div className="absolute inset-0 overflow-hidden rounded-full">
            <AvatarComponent
              username={processedUsername}
              showSkeleton={false}
              className="size-12 transition-all duration-300"
              fallbackBg={cor}
            />
          </div>
        </div>
      </HoverCardTrigger>

      <HoverCardContent className="w-72">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div
              className={`size-24 rounded-full flex items-center justify-center font-medium text-lg transition-all duration-300 ${softColor}`}
            >
              {fmt.strings.initials(row.original.F1_XUSRRA)}
            </div>

            <div className="absolute inset-0 rounded-lg overflow-hidden">
              <AvatarComponent
                username={processedUsername}
                showSkeleton={false}
                className="size-24 rounded-lg transition-all duration-300"
                fallbackBg={cor}
              />
            </div>
          </div>

          <div className="min-w-0">
            <p
              className={`font-medium truncate ${CELL_CLASSES.responsiveText}`}
            >
              {fmt.strings.capitalize(row.original.F1_XUSRRA)}
            </p>
            {row.original.F1_XORI && (
              <p
                className={`${CELL_CLASSES.responsiveText} text-muted-foreground line-clamp-3`}
              >
                {row.original.F1_XORI}
              </p>
            )}
            {row.original.F1_XOBS && (
              <p
                className={`${CELL_CLASSES.responsiveText} text-muted-foreground line-clamp-3`}
              >
                {row.original.F1_XOBS}
              </p>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

/**
 * Componente de célula de ações com dialog de edição
 */
export function ActionsCell({ row }: { row: Row<PrenotaRow> }) {
  // Debug: log para verificar se está renderizando
  // console.log(`ActionsCell REC${row.original.REC}:`, {
  //   REC: row.original.REC,
  //   Z07_CHAVE: row.original.Z07_CHAVE,
  // });

  return (
    <div className="flex justify-end gap-2">
      <AnexoPopover rec={row.original.REC} z07Chave={row.original.Z07_CHAVE} />
      <EditPrenotaDialog rec={row.original.REC} />
      <Actions preNota={row.original} />
      <Danfe rec={row.original.REC} z07Chave={row.original.F1_CHVNFE} />
    </div>
  );
}
